import {CopyToClipboardIconButton} from '@github-ui/copy-to-clipboard/IconButton'
import {CopilotAppOpenAction, CopilotAppOpenActionMenuItem} from '@github-ui/copilot-app-launcher-shared'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ListView} from '@github-ui/list-view'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {McpInstallButton, type McpInstallTarget} from '@github-ui/mcp-install-button'
import {CopilotIcon, TerminalIcon, TriangleDownIcon, type Icon} from '@primer/octicons-react'
import {ActionList, ActionMenu, AnchoredOverlay, Button, Link} from '@primer/react'
import {useId, useState} from 'react'

import styles from './AIResourceResults.module.css'
import type {AIResourcePluginMarketplace} from './types'

export type AIResourceInstallAction =
  | {type: 'deeplink'; command: string; target: string}
  | {type: 'mcp_server'; target: McpInstallTarget; command: string}
  | {type: 'skill'; command: string; hasCompanionFiles: boolean}
  | {type: 'plugin'; command: string; marketplaces: readonly AIResourcePluginMarketplace[]}

export type AIResourceResult = {
  id: string
  /** An authorized canonical destination supplied by the composing application. */
  href: string
  name: string
  description?: string
  resourceType: string
  resourceTypeIcon?: Icon
  publisher?: {
    name: string
    /** An authorized canonical destination supplied by the composing application. */
    href: string
  }
  labels: readonly string[]
  installAction?: AIResourceInstallAction
  connectorIds?: readonly string[]
}

export type AIResourceResultsProps = {
  results: readonly AIResourceResult[]
}

export function AIResourceResults({results}: AIResourceResultsProps) {
  if (results.length === 0) return null

  return (
    <ListView itemsListClassName={styles.List} title="AI resources" totalCount={results.length}>
      {results.map(result => (
        <AIResourceResultRow key={result.id} result={result} />
      ))}
    </ListView>
  )
}

export function AIResourceResultRow({result}: {result: AIResourceResult}) {
  const ResourceTypeIcon = result.resourceTypeIcon
  const canViewConnector =
    isFeatureEnabled('agentfinder_connectors_ghapp_deeplink') && (result.connectorIds?.length ?? 0) > 0

  return (
    <ListItem
      className={styles.Row}
      title={<ListItemTitle href={result.href} value={result.name} linkProps={{as: Link, className: styles.Link}} />}
      metadata={
        result.installAction || canViewConnector ? (
          <InstallAction action={result.installAction} name={result.name} canViewConnector={canViewConnector} />
        ) : undefined
      }
      metadataContainerClassName={styles.InstallMetadata}
    >
      <ListItemMainContent>
        <div className={styles.Content}>
          {result.description && (
            <ListItemDescription>
              <ListItemDescriptionItem>{result.description}</ListItemDescriptionItem>
            </ListItemDescription>
          )}

          <ListItemDescription>
            <span className={styles.Metadata}>
              <span className={styles.ResourceType}>
                {ResourceTypeIcon && <ResourceTypeIcon aria-hidden="true" data-testid="resource-type-icon" />}
                <span>{result.resourceType}</span>
              </span>
              {result.publisher && (
                <>
                  <span className={styles.Separator} aria-hidden="true" />
                  <Link className={styles.Link} href={result.publisher.href}>
                    {result.publisher.name}
                  </Link>
                </>
              )}
            </span>
          </ListItemDescription>
        </div>
      </ListItemMainContent>
    </ListItem>
  )
}

function GitHubAppConnectorAction({inMenu = true}: {inMenu?: boolean}) {
  const AppAction = inMenu ? CopilotAppOpenActionMenuItem : CopilotAppOpenAction
  return (
    <AppAction
      open="ghapp://extensions/connectors"
      entryPoint="dotcom_ai-resource-search_connectors"
      // eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- GitHub App is the product name.
      label="View in GitHub App"
    />
  )
}

function InstallAction({
  action,
  name,
  canViewConnector,
}: {
  action?: AIResourceInstallAction
  name: string
  canViewConnector: boolean
}) {
  if (action?.type === 'mcp_server') {
    return <McpInstallAction action={action} name={name} canViewConnector={canViewConnector} />
  }

  if (action?.type === 'deeplink') {
    return <DeeplinkInstallAction action={action} name={name} canViewConnector={canViewConnector} />
  }

  if (action) {
    return <CLIInstallAction action={action} name={name} canViewConnector={canViewConnector} />
  }

  return (
    <ActionMenu>
      <ActionMenu.Button size="small" aria-label={`Install ${name}`}>
        Install
      </ActionMenu.Button>
      <ActionMenu.Overlay>
        <ActionList>
          <GitHubAppConnectorAction />
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function DeeplinkInstallAction({
  action,
  name,
  canViewConnector,
}: {
  action: Extract<AIResourceInstallAction, {type: 'deeplink'}>
  name: string
  canViewConnector: boolean
}) {
  const [open, setOpen] = useState(false)
  const headingId = useId()
  const cliHeadingId = useId()

  return (
    <AnchoredOverlay
      align="end"
      focusZoneSettings={{disabled: true}}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      open={open}
      overlayProps={{
        'aria-labelledby': headingId,
        'aria-modal': true,
        role: 'dialog',
      }}
      renderAnchor={anchorProps => (
        <Button
          {...anchorProps}
          aria-haspopup="dialog"
          aria-label={`Install ${name}`}
          size="small"
          trailingVisual={TriangleDownIcon}
        >
          Install
        </Button>
      )}
      width="large"
    >
      <section className={styles.InstallPanelSection} aria-labelledby={headingId}>
        <h4 className={styles.SkillInstallHeading} id={headingId}>
          <CopilotIcon aria-hidden="true" />
          <span>Copilot App</span>
        </h4>
        <Button
          as="a"
          alignContent="start"
          block
          href={`/copilot/app/launch?open=${encodeURIComponent(action.target)}&entry_point=dotcom_ai-resource-search_install`}
          leadingVisual={CopilotIcon}
        >
          {/* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- Copilot App is the product name. */}
          {'Install in Copilot App'}
        </Button>
        {canViewConnector && (
          <ActionList>
            <GitHubAppConnectorAction inMenu={false} />
          </ActionList>
        )}
      </section>
      <hr className={styles.InstallPanelDivider} />
      <section className={styles.InstallPanelSection} aria-labelledby={cliHeadingId}>
        <h4 className={styles.SkillInstallHeading} id={cliHeadingId}>
          <TerminalIcon aria-hidden="true" data-testid="deeplink-install-terminal-icon" />
          <span>Copilot CLI</span>
        </h4>
        <CLIInstallOption
          title="Install with Copilot CLI"
          description="Run this command in your terminal. Copilot will verify the resource and ask before installing it."
          command={action.command}
        />
      </section>
    </AnchoredOverlay>
  )
}

function McpInstallAction({
  action,
  name,
  canViewConnector,
}: {
  action: Extract<AIResourceInstallAction, {type: 'mcp_server'}>
  name: string
  canViewConnector: boolean
}) {
  return (
    <McpInstallButton
      mcp={action.target}
      muted
      layout="panel"
      ariaLabel={`Install ${name}`}
      analyticsResourceName={name}
      copilotAppEntryPoint="dotcom_ai-resource-search_install"
      footer={
        <>
          <h4 className={styles.SkillInstallHeading}>
            <TerminalIcon aria-hidden="true" data-testid="mcp-install-terminal-icon" />
            <span>{canViewConnector ? 'Install options' : 'Copilot CLI'}</span>
          </h4>
          <CLIInstallOption
            title="Find MCP server"
            description="Select the server from the search results and complete any required configuration. It may not appear if your CLI uses an organization-configured registry."
            command={action.command}
          />
          {canViewConnector && (
            <ActionList>
              <GitHubAppConnectorAction inMenu={false} />
            </ActionList>
          )}
        </>
      }
    />
  )
}

function CLIInstallAction({
  action,
  name,
  canViewConnector,
}: {
  action: Exclude<AIResourceInstallAction, {type: 'mcp_server'} | {type: 'deeplink'}>
  name: string
  canViewConnector: boolean
}) {
  const [open, setOpen] = useState(false)
  const headingId = useId()

  return (
    <AnchoredOverlay
      align="end"
      focusZoneSettings={{disabled: true}}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      open={open}
      overlayProps={{
        'aria-labelledby': headingId,
        'aria-modal': true,
        role: 'dialog',
      }}
      renderAnchor={anchorProps => (
        <Button
          {...anchorProps}
          aria-haspopup="dialog"
          aria-label={`Install ${name}`}
          size="small"
          trailingVisual={TriangleDownIcon}
        >
          Install
        </Button>
      )}
      width="large"
    >
      <div className={styles.SkillInstallMenu}>
        <h4 className={styles.SkillInstallHeading} id={headingId}>
          <TerminalIcon aria-hidden="true" data-testid="skill-install-terminal-icon" />
          <span>{canViewConnector ? 'Install options' : 'Copilot CLI'}</span>
        </h4>
        {action.type === 'skill' ? (
          <CLIInstallOption
            title={action.hasCompanionFiles ? 'Install skill with supporting files' : 'Install skill'}
            description={
              action.hasCompanionFiles
                ? 'Clone the repository to include the supporting files this skill requires. Keep the clone in place.'
                : 'This skill doesn’t require any supporting files.'
            }
            command={action.command}
            multiline={action.hasCompanionFiles}
          />
        ) : (
          <div className={styles.CLIInstallOptions}>
            {action.marketplaces.map(marketplace => (
              <section
                className={styles.SkillInstallOption}
                key={`${marketplace.source}:${marketplace.name}:${marketplace.entry}`}
              >
                <h5 className={styles.SkillInstallOptionHeading}>Install via {marketplace.name} marketplace</h5>
                <p className={styles.SkillInstallDescription}>Run these commands in order in your terminal.</p>
                <div className={styles.PluginMarketplaceSteps}>
                  <div className={styles.PluginMarketplaceStep}>
                    <p className={styles.SkillInstallDescription}>First, add the marketplace.</p>
                    <CLIInstallCommand
                      ariaLabel={`Add ${marketplace.name} marketplace command`}
                      command={`copilot plugin marketplace add ${marketplace.source}`}
                      copyLabel={`Copy ${marketplace.name} marketplace add command to clipboard`}
                    />
                  </div>
                  <div className={styles.PluginMarketplaceStep}>
                    <p className={styles.SkillInstallDescription}>Then, install the verified marketplace entry.</p>
                    <CLIInstallCommand
                      ariaLabel={`Install ${marketplace.entry} from ${marketplace.name} command`}
                      command={`copilot plugin install ${marketplace.entry}@${marketplace.name}`}
                      copyLabel={`Copy ${marketplace.entry} from ${marketplace.name} install command to clipboard`}
                    />
                  </div>
                </div>
              </section>
            ))}
            <CLIInstallOption
              title={action.marketplaces.length > 0 ? 'Or install directly' : 'Install plugin from repository'}
              description="Run this command in your terminal to install the verified plugin directory."
              command={action.command}
              copyLabel="Copy repository install command to clipboard"
            />
          </div>
        )}
        {canViewConnector && (
          <ActionList>
            <GitHubAppConnectorAction inMenu={false} />
          </ActionList>
        )}
      </div>
    </AnchoredOverlay>
  )
}

function CLIInstallOption({
  title,
  description,
  command,
  multiline = false,
  copyLabel = 'Copy command to clipboard',
}: {
  title: string
  description: string
  command: string
  multiline?: boolean
  copyLabel?: string
}) {
  return (
    <section className={styles.SkillInstallOption}>
      <h5 className={styles.SkillInstallOptionHeading}>{title}</h5>
      <p className={styles.SkillInstallDescription}>{description}</p>
      <CLIInstallCommand ariaLabel={`${title} command`} command={command} copyLabel={copyLabel} multiline={multiline} />
    </section>
  )
}

function CLIInstallCommand({
  ariaLabel,
  command,
  copyLabel,
  multiline = false,
}: {
  ariaLabel: string
  command: string
  copyLabel: string
  multiline?: boolean
}) {
  return (
    <div className={styles.SkillInstallCommand}>
      <pre
        aria-label={ariaLabel}
        className={`${styles.SkillInstallCode} ${multiline ? styles.SkillInstallMultilineCode : ''}`}
        role="region"
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- keyboard-scrollable overflow region
        tabIndex={0}
      >
        <code>{command}</code>
      </pre>
      <CopyToClipboardIconButton
        className={styles.SkillInstallCopyButton}
        textToCopy={command}
        ariaLabel={copyLabel}
        tooltipProps={{direction: 'nw'}}
      />
    </div>
  )
}
